import { useState, type MouseEvent, type ReactNode } from 'react';
import { DarkMode, KeyboardArrowUp, LightMode, SettingsBrightness } from '@mui/icons-material';
import {
  Button,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
  type PopoverOrigin,
  type SxProps,
  type Theme,
} from '@mui/material';
import { useSharedTFunction } from '@shared/i18n';
import { useThemeMode } from './useThemeMode';
import type { ThemeMode } from './withColorSchemes';

const THEME_MODES = ['light', 'system', 'dark'] as const satisfies readonly ThemeMode[];

const themeModeIcons = {
  light: LightMode,
  system: SettingsBrightness,
  dark: DarkMode,
} as const;

const menuOrigins = {
  bottom: {
    anchorOrigin: { vertical: 'bottom', horizontal: 'right' },
    transformOrigin: { vertical: 'top', horizontal: 'right' },
  },
  top: {
    anchorOrigin: { vertical: 'top', horizontal: 'left' },
    transformOrigin: { vertical: 'bottom', horizontal: 'left' },
  },
} as const satisfies Record<
  string,
  { anchorOrigin: PopoverOrigin; transformOrigin: PopoverOrigin }
>;

export type ThemeModeToggleProps = {
  variant?: 'button' | 'menu';
  size?: 'small' | 'medium';
  fullWidth?: boolean;
  menuPlacement?: keyof typeof menuOrigins;
  icon?: ReactNode;
  sx?: SxProps<Theme>;
};

export function ThemeModeToggle({
  variant = 'button',
  size = 'small',
  fullWidth = false,
  menuPlacement = 'bottom',
  icon,
  sx,
}: ThemeModeToggleProps) {
  const t = useSharedTFunction();
  const { mode, setMode } = useThemeMode();
  const [anchorElement, setAnchorElement] = useState<HTMLElement | null>(null);

  if (!mode) {
    return null;
  }

  const labelFor = (value: ThemeMode) => t(`shared.themeMode.${value}`);
  const CurrentIcon = themeModeIcons[mode];
  const { anchorOrigin, transformOrigin } = menuOrigins[menuPlacement];
  const openMenu = (event: MouseEvent<HTMLButtonElement>) => setAnchorElement(event.currentTarget);

  return (
    <>
      {variant === 'menu' ? (
        <Tooltip title={`${t('shared.themeMode.label')}: ${labelFor(mode)}`}>
          <IconButton
            size={size}
            sx={sx}
            aria-label={t('shared.themeMode.label')}
            aria-haspopup="menu"
            aria-expanded={Boolean(anchorElement)}
            onClick={openMenu}
          >
            {icon ?? <CurrentIcon fontSize="small" />}
          </IconButton>
        </Tooltip>
      ) : (
        <Button
          fullWidth={fullWidth}
          size={size}
          color="inherit"
          aria-label={t('shared.themeMode.label')}
          aria-haspopup="menu"
          aria-expanded={Boolean(anchorElement)}
          onClick={openMenu}
          sx={[
            (theme) => ({
              justifyContent: 'space-between',
              textTransform: 'none',
              color: 'text.primary',
              px: 1.5,
              py: 1,
              borderRadius: 2,
              bgcolor: (theme.vars || theme).palette.grey[100],
              '&:hover': {
                bgcolor: (theme.vars || theme).palette.grey[200],
              },
              ...theme.applyStyles('dark', {
                bgcolor: 'rgba(255,255,255,0.06)',
                '&:hover': {
                  bgcolor: 'rgba(255,255,255,0.1)',
                },
              }),
            }),
            ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
          ]}
        >
          <Stack direction="row" alignItems="center" spacing={1.25} minWidth={0}>
            <CurrentIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
            <Stack alignItems="flex-start" minWidth={0}>
              <Typography variant="caption" color="text.secondary" lineHeight={1.2}>
                {t('shared.themeMode.label')}
              </Typography>
              <Typography variant="body2" fontWeight={600} lineHeight={1.3} noWrap>
                {labelFor(mode)}
              </Typography>
            </Stack>
          </Stack>
          <KeyboardArrowUp fontSize="small" sx={{ color: 'text.secondary', ml: 1 }} />
        </Button>
      )}
      <Menu
        anchorEl={anchorElement}
        open={Boolean(anchorElement)}
        onClose={() => setAnchorElement(null)}
        anchorOrigin={anchorOrigin}
        transformOrigin={transformOrigin}
      >
        {THEME_MODES.map((value) => {
          const Icon = themeModeIcons[value];
          return (
            <MenuItem
              key={value}
              selected={mode === value}
              onClick={() => {
                setMode(value);
                setAnchorElement(null);
              }}
            >
              <ListItemIcon>
                <Icon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary={labelFor(value)} />
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
}
